import Button from 'components/Shared/Button'
import {
  CurrentUser,
  getCurrentUser,
  UserState,
  validateUserEmail,
} from 'ducks/users'
import { Link, useHistory, useLocation } from 'react-router-dom'
import { InjectedFormProps } from 'redux-form'
import Loading from 'components/Shared/Loading'
import QS from 'qs'
import { connect, DefaultRootState, useDispatch } from 'react-redux'
import { FormEvent, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import classNames from 'classnames'
import { adaloBackendAxios } from 'utils/io/http/axios'
import AdaloLogotype from './AdaloLogotype'
import OnboardingLayout from './Layout'
import TranscosmosLogotype from './TranscosmosLogotype'

type ValidationFormValues = {
  emailValidation: string
}

type Props = {
  currentUser: CurrentUser
}

const UserEmailValidation = ({
  currentUser,
}: Props & InjectedFormProps<ValidationFormValues, Props>) => {
  const location = useLocation()
  const history = useHistory()
  const dispatch = useDispatch()
  const { t, i18n } = useTranslation()
  const isJapanese = useMemo(() => i18n.language === 'ja', [i18n.language])
  const queryParams = QS.parse(location.search, { ignoreQueryPrefix: true })
  const { redirect, token } = queryParams
  const redirectQuery = QS.stringify({ redirect }, { addQueryPrefix: true })
  const givenCode = window.location.search.split('?code=')[1]
  const [resendError, setResendError] = useState('')
  const [buttonDisabled, setButtonDisabled] = useState(false)

  const navigateToOnboarding = () => {
    // eslint-disable-next-line @typescript-eslint/restrict-template-expressions
    const redirectURL = token ? `/?token=${token}` : '/'
    history.push(redirectURL)
  }

  if (currentUser?.email && givenCode) {
    dispatch(
      validateUserEmail({
        code: givenCode,
        email: currentUser?.email,
        callback: navigateToOnboarding,
      })
    )
  }

  if (!currentUser) {
    return (
      <OnboardingLayout>
        <Loading expanded />
      </OnboardingLayout>
    )
  }

  const resendEmail = (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault()
    setButtonDisabled(true)
    adaloBackendAxios
      .post(`/users/send-validation-email`, {
        email: currentUser?.email,
        region: isJapanese ? 'japan' : null,
      })
      .catch((err: unknown): void =>
        setResendError((err?.message || err) as string)
      )
      .finally(() => {
        setButtonDisabled(false)
      })
  }

  return (
    <OnboardingLayout>
      <form
        onSubmit={resendEmail}
        className={classNames('auth-form-v2', {
          'auth-form-v2--transcosmos': isJapanese,
        })}
      >
        <div
          className={classNames('auth-form-v2__header', {
            'auth-form-v2__header--transcosmos': isJapanese,
          })}
        >
          <div className="auth-form-v2__header-logos">
            <AdaloLogotype />
            {isJapanese && <TranscosmosLogotype />}
          </div>
          <h1>{t('Validate Your Email')}</h1>
        </div>
        <p>
          {t(
            "Please click the link sent to your email. If you don't see it, check your spam folder for an email from support@adalo.com. Click the button below to try again."
          )}
        </p>
        <div className="auth-form-v2__content">
          <div className="auth-form-submission-row">
            <Button
              square
              gradient
              large
              teal={!isJapanese}
              transcosmosRed={isJapanese}
              fluid
              loading={buttonDisabled}
              disabled={buttonDisabled}
            >
              {t('Resend email')}
            </Button>
          </div>
          {resendError && <div className="auth-form-error">{resendError}</div>}
        </div>
        <div className="auth-form-v2__footer">
          <div
            className={classNames('auth-form-link-v2', {
              'auth-form-link-v2--transcosmos': isJapanese,
            })}
          >
            {`${t('Already have an account?')} `}
            <Link to={`/login${redirectQuery}`} className="link-button">
              {t('Login')}
            </Link>
          </div>
        </div>
      </form>
    </OnboardingLayout>
  )
}

const mapStateToProps = (state: DefaultRootState) => {
  const currentUser = getCurrentUser(state as UserState)

  return {
    currentUser,
  }
}
export default connect<
  ReturnType<typeof mapStateToProps>,
  { validateUserEmail: typeof validateUserEmail },
  Props
>(mapStateToProps, { validateUserEmail })(UserEmailValidation)
